<template>
    <div>
        <div class="flex flex-col gap-4">
            <div class="font-semibold">
                {{ $t('checkout.payment.headline') }}
            </div>

            <Transition name="fade" mode="out-in">
                <div v-if="loading" class="flex flex-col gap-4">
                    <div class="flex gap-4">
                        <MiscSkeleton circle size="large" />
                        <MiscSkeleton text size="large" />
                    </div>
                    <div class="flex gap-4">
                        <MiscSkeleton circle size="large" />
                        <MiscSkeleton text size="large" />
                    </div>
                    <div class="flex gap-4">
                        <MiscSkeleton circle size="large" />
                        <MiscSkeleton text size="large" />
                    </div>
                </div>

                <div v-else-if="error">
                    {{ error }}
                </div>

                <div v-else-if="displayedPaymentMethods != null" class="flex flex-col border rounded-xs divide-y overflow-hidden">
                    <label
                        v-for="method in displayedPaymentMethods"
                        :key="method.id"
                        :for="'payment-option-' + method.id"
                        :class="{
                            'ring-1 ring-inset ring-primary': selectedMethodId === method.id,
                            'opacity-50': updateLoading
                        }"
                        class="flex justify-between items-center gap-4 p-4 bg-base-100 cursor-pointer"
                    >
                        <input
                            :id="'payment-option-' + method.id"
                            v-model="selectedMethodId"
                            :value="method.id"
                            type="radio"
                            class="radio checked:bg-primary w-6"
                            :disabled="updateLoading"
                            @change="handleMethodChange(method.id)"
                        >
                        <span class="w-full mr-auto flex flex-col gap-2 text-sm">
                            <span class="font-semibold">{{ method?.translated?.name }}</span>
                            <span v-if="method?.translated?.description ?? method?.description" class="text-base-content-subtle">{{ method?.translated?.description ?? method?.description }}</span>

                            <MollieCard
                                v-if="method.translated?.customFields?.mollie_payment_method_name === 'creditcard' && mollieInit"
                                v-show="selectedMethodId === method.id"
                            />

                            <StripeElement
                                v-if="method.shortName === 'stripe_payments_app_stripe_payment' && stripeInit"
                                v-show="selectedMethodId === method.id"
                            />
                        </span>
                        <NuxtImg
                            v-if="method.media != null"
                            class="max-w-[70px]"
                            :src="method.media.url"
                            :alt="method.media?.translated?.alt ?? method.media?.alt"
                        />
                    </label>
                </div>
            </Transition>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import {
    useCart,
    useCheckout,
    useI18n,
    useNotification,
    useNuxtApp,
    useSessionContext,
} from '#imports'
import { useApplePayAvailability } from '~/composables/useApplePayAvailability'

const emit = defineEmits<{
    (event: 'updateBefore:paymentMethod' | 'updateAfter:paymentMethod', id: string): void
}>()

const nuxtApp = useNuxtApp()
const { showNotification } = useNotification()
const { t } = useI18n()
const { paymentMethods, getPaymentMethods } = useCheckout()
const { selectedPaymentMethod, setPaymentMethod } = useSessionContext()
const { cart } = useCart()
const applePayAvailable = useApplePayAvailability()

const props = defineProps({
    currentStep: {
        type: String,
        default: ''
    },
    orderId: {
        type: String,
        default: undefined
    }
})

// State
const loading = ref(false)
const updateLoading = ref(false)
const error = ref<string | null>(null)
const mollieInit = ref(false)
const stripeInit = ref(false)

// Computed
const displayedPaymentMethods = computed(() => {
    const methods = paymentMethods?.value
    if (methods == null) return null
    if (applePayAvailable.value) return methods
    return methods.filter(method => method.translated?.customFields?.mollie_payment_method_name !== 'applepay')
})

const selectedMethodId = computed({
    get(): string {
        return selectedPaymentMethod.value?.id || ''
    },
    set(_methodId: string) {
        // This setter is mainly for form binding, actual logic is in handleMethodChange
    }
})

const handleMethodChange = async (_methodId: string) => {
    if (updateLoading.value || selectedMethodId.value === _methodId) return

    error.value = null
    updateLoading.value = true
    emit('updateBefore:paymentMethod', _methodId)

    try {
        await setPaymentMethod({ id: _methodId })
        nuxtApp.callHook('selectPaymentMethod', { payment: paymentMethods?.value?.find(method => method.id === _methodId), cart: cart.value })
        emit('updateAfter:paymentMethod', _methodId)
    } catch (err) {
        error.value = err instanceof Error ? err.message : t('checkout.payment.updateError')
        showNotification(error.value, 'error')
    } finally {
        updateLoading.value = false
    }
}

watch(() => props.currentStep, (value) => {
    if (value === 'payment') {
        nuxtApp.callHook('selectPaymentMethod', { payment: paymentMethods?.value?.find(method => method.id === selectedMethodId.value), cart: cart.value })
        mollieInit.value = true
        stripeInit.value = true
    }
})

onMounted(async () => {
    error.value = null
    loading.value = true

    try {
        await getPaymentMethods(props.orderId)

        if (props.currentStep === 'payment') {
            mollieInit.value = true
            stripeInit.value = true
        }

        // If the session's selected method is not in the available list, select the first available
        const isAvailable = paymentMethods.value?.some(method => method.id === selectedMethodId.value)
        const firstMethodId = paymentMethods.value?.[0]?.id
        if (!isAvailable && firstMethodId) {
            await setPaymentMethod({ id: firstMethodId })
        }
    } catch (err) {
        error.value = err instanceof Error ? err.message : t('checkout.payment.loadError')
        showNotification(error.value, 'error')
    } finally {
        loading.value = false
    }
})
</script>